import { useState } from 'react';
import { type FanGender } from '../api/fan-api';

/**
 * Reusable fan-profile form body — extracted from
 * FanProfileSetupModal so the same fields render inside two
 * contexts:
 *   1. The first-login blocking modal (no cancel)
 *   2. The Profile-page edit sheet (with cancel)
 *
 * displayName is required (server enforces min 1); birthday + gender
 * optional. Parent decides what to do with the submitted patch via
 * onSubmit — typically a PUT /api/fan/me followed by some local
 * state update.
 */

export interface FanProfileFormValues {
  displayName: string;
  birthday?: string;
  gender?: FanGender;
}

interface Props {
  /** Pre-fill values (e.g. from current GET /api/fan/me). */
  initial?: Partial<FanProfileFormValues>;
  /** Async submit. Resolves on success, throws on failure (error
   *  surface inline). */
  onSubmit: (values: FanProfileFormValues) => Promise<void>;
  /** Optional cancel handler — when present, renders a 取消 button
   *  alongside the submit. The first-login modal doesn't pass this
   *  (the modal is blocking-by-design). */
  onCancel?: () => void;
  /** Override the submit button label. Defaults to "完成設定". */
  submitLabel?: string;
}

const GENDER_OPTIONS: Array<{ value: FanGender; label: string }> = [
  { value: 'male', label: '男' },
  { value: 'female', label: '女' },
  { value: 'other', label: '其他' },
  { value: 'prefer-not-to-say', label: '不願透露' },
];

export function FanProfileForm({ initial, onSubmit, onCancel, submitLabel }: Props) {
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const [birthday, setBirthday] = useState(initial?.birthday ?? '');
  const [gender, setGender] = useState<FanGender | ''>(initial?.gender ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canSubmit = displayName.trim().length > 0 && !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);
    try {
      await onSubmit({
        displayName: displayName.trim(),
        ...(birthday ? { birthday } : {}),
        ...(gender ? { gender: gender as FanGender } : {}),
      });
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : '儲存失敗，請再試一次');
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <label className="block">
        <span className="text-sm font-medium text-slate-200">
          暱稱 <span className="text-rose-400">*</span>
        </span>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={80}
          required
          autoFocus
          placeholder="顯示給其他人看的名字"
          className="mt-1 block w-full rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium text-slate-200">生日（選填）</span>
        <input
          type="date"
          value={birthday}
          onChange={(e) => setBirthday(e.target.value)}
          className="mt-1 block w-full rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
        />
      </label>

      <fieldset>
        <legend className="text-sm font-medium text-slate-200">性別（選填）</legend>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {GENDER_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center justify-center rounded-md border px-3 py-2 text-sm cursor-pointer transition ${
                gender === opt.value
                  ? 'border-violet-500 bg-violet-500/15 text-violet-200'
                  : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500'
              }`}
            >
              <input
                type="radio"
                name="gender"
                value={opt.value}
                checked={gender === opt.value}
                onChange={() => setGender(opt.value)}
                className="sr-only"
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      {err && (
        <div className="rounded-md bg-rose-500/15 border border-rose-500/40 px-3 py-2 text-sm text-rose-200">
          {err}
        </div>
      )}

      <div className="flex gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium py-2.5 transition disabled:opacity-50"
          >
            取消
          </button>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          className={`${onCancel ? 'flex-[2]' : 'w-full'} rounded-md bg-violet-500 hover:bg-violet-400 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium py-2.5 transition`}
        >
          {submitting ? '儲存中…' : (submitLabel ?? '完成設定')}
        </button>
      </div>
    </form>
  );
}
